import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { getCurrentSession, refreshSession } from './cognito'
import { emitSessionExpiry } from '../utils/sessionExpiry'
import { getDeviceFingerprint } from '../utils/deviceFingerprint'

/**
 * Dedicated error type for session expiry.
 * Thrown by the interceptor when token refresh fails — this is a first-class
 * control-flow event, not a normal request error. Thunks should detect this
 * and avoid writing to state.error. SessionExpiryHandler owns the UX.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super('SESSION_EXPIRED')
    this.name = 'SessionExpiredError'
  }
}

/** Sentinel value for thunk rejectWithValue when session has expired */
export const SESSION_EXPIRED_SENTINEL = '__SESSION_EXPIRED__' as const

/** Check if an error (from catch) or a rejected payload is a session expiry */
export function isSessionExpired(error: unknown): boolean {
  if (error instanceof SessionExpiredError) return true
  if (typeof error === 'object' && error !== null && (error as any).name === 'SessionExpiredError') return true
  return false
}

const API_URL = process.env.REACT_APP_API_URL!

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Store token to avoid infinite loops
let cachedToken: string | null = null

// Cache device fingerprint
let cachedFingerprint: string | null = null

// Single-flight refresh: one promise shared by all concurrent failed requests
let refreshPromise: Promise<string | null> | null = null

/**
 * Detect whether an error is an auth-expiry (expired/invalid token) vs a real business 403.
 *
 * API Gateway authorizer Deny returns: { "message": "Forbidden" } or
 * { "message": "User is not authorized to access this resource with an explicit deny" }
 *
 * Our Lambda handlers return business 403s as: { "error": "Access denied" } etc.
 */
function isAuthExpiry(error: AxiosError): boolean {
  const status = error.response?.status
  if (status === 401) return true
  if (status === 403) {
    const body = error.response?.data as any
    const msg = (body?.message || '').toLowerCase()
    // API Gateway authorizer responses — not our application errors
    if (msg.includes('forbidden') || msg.includes('unauthorized') || msg.includes('not authorized')) {
      return true
    }
    // If response has no body or is HTML (API Gateway default error page), treat as auth
    if (!body || typeof body === 'string') return true
  }
  return false
}

/**
 * Attempt silent token refresh. Returns new access token or null on failure.
 * Uses single-flight pattern: concurrent calls share the same promise.
 */
function doRefresh(): Promise<string | null> {
  if (refreshPromise) return refreshPromise

  refreshPromise = refreshSession()
    .then(session => {
      const newToken = session?.accessToken || null
      cachedToken = newToken
      return newToken
    })
    .catch(() => {
      cachedToken = null
      return null
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

// Add Cognito access token and device fingerprint to requests
api.interceptors.request.use(async config => {
  try {
    // Only fetch session if we don't have a cached token
    if (!cachedToken) {
      const session = await getCurrentSession()
      if (session && session.accessToken) {
        cachedToken = session.accessToken
      }
    }

    if (cachedToken) {
      config.headers.Authorization = `Bearer ${cachedToken}`
    }

    // Add device fingerprint to payment-related requests for trial abuse prevention
    const isPaymentRequest = config.url?.includes('/payments') || config.url?.includes('/checkout')
    if (isPaymentRequest) {
      if (!cachedFingerprint) {
        cachedFingerprint = await getDeviceFingerprint()
      }
      config.headers['X-Device-Fingerprint'] = cachedFingerprint
    }
  } catch (error) {
    cachedToken = null
  }
  return config
})

// Response interceptor: silent token refresh on auth expiry, retry once
api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Only attempt refresh for auth-expiry errors, and only once per request
    if (originalRequest && isAuthExpiry(error) && !originalRequest._retry) {
      originalRequest._retry = true

      const newToken = await doRefresh()

      if (newToken) {
        // Refresh succeeded — retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      }

      // Refresh failed — session is truly expired, force logout
      cachedToken = null
      emitSessionExpiry('user', 'Session expired. Please log in again.')
      // Reject with dedicated error type — thunks should NOT store this in state.error
      return Promise.reject(new SessionExpiredError())
    }

    return Promise.reject(error)
  }
)

// Export function to update token cache
export function updateApiToken(token: string | null) {
  cachedToken = token
}

// Auth API
export const authAPI = {
  register: async (email: string, password: string) => {
    const response = await api.post('/auth/register', { email, password })
    return response.data
  },

  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password })
    return response.data
  }
}

// Properties API
export const propertiesAPI = {
  list: async (filters?: { location?: string, priceMin?: number, priceMax?: number, page?: number }) => {
    const response = await api.get('/properties', { params: filters })
    return response.data
  },

  search: async (params: {
    strategy?: string,
    minYield?: number, maxYield?: number,
    minRent?: number, maxRent?: number,
    minPrice?: number, maxPrice?: number,
    bedrooms?: string,
    propertyType?: string,
    minScore?: number, maxScore?: number,
    qualityRating?: string,
    minCrime?: number, maxCrime?: number,
    sort?: string,
    cursor?: string,
    pageSize?: number,
    fields?: string,
  }) => {
    const response = await api.get('/properties/search', { params })
    return response.data
  },

  get: async (propertyId: string) => {
    const response = await api.get(`/properties/${propertyId}`)
    return response.data
  },

  unlock: async (propertyId: string) => {
    const response = await api.post(`/properties/${propertyId}/unlock`)
    return response.data
  },

  getUnlocked: async () => {
    const response = await api.get('/users/me/unlocks')
    return response.data
  },

  toggleFavourite: async (propertyId: string) => {
    const response = await api.post(`/properties/${propertyId}/favourite`)
    return response.data
  },

  getFavourites: async () => {
    const response = await api.get('/users/me/favourites')
    return response.data
  },

  getPreferences: async () => {
    const response = await api.get('/users/me/preferences')
    return response.data
  },

  updatePreferences: async (preferences: any) => {
    const response = await api.put('/users/me/preferences', preferences)
    return response.data
  },

  getRecommended: async () => {
    const response = await api.get('/users/me/recommended')
    return response.data
  }
}

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats')
    return response.data
  },
}

// Credits API
export const creditsAPI = {
  initialize: async (referralCode?: string) => {
    const response = await api.post('/credits/initialize', referralCode ? { referralCode } : {})
    return response.data
  },

  getBalance: async () => {
    const response = await api.get('/credits/balance')
    return response.data
  },

  getHistory: async () => {
    const response = await api.get('/credits/history')
    return response.data
  },

  getFeatures: async () => {
    const response = await api.get('/credits/features')
    return response.data
  },

  cancelSubscription: async () => {
    const response = await api.delete('/credits/subscription')
    return response.data
  }
}

// Payments API
export const paymentsAPI = {
  createCheckout: async (type: 'credits' | 'subscription', productId: string) => {
    const response = await api.post('/payments/checkout', { type, productId })
    return response.data
  },

  getCredits: async () => {
    const response = await api.get('/payments/credits')
    return response.data
  }
}

// Filter Presets API
export const filterPresetsAPI = {
  getAll: async () => {
    const response = await api.get('/users/me/filter-presets')
    return response.data
  },

  save: async (preset: { name: string; description?: string; filters: any }) => {
    const response = await api.post('/users/me/filter-presets', preset)
    return response.data
  },

  update: async (id: string, updates: { name?: string; description?: string; filters?: any }) => {
    const response = await api.put(`/users/me/filter-presets/${id}`, updates)
    return response.data
  },

  delete: async (id: string) => {
    const response = await api.delete(`/users/me/filter-presets/${id}`)
    return response.data
  }
}

// Feedback API
export const feedbackAPI = {
  submit: async (type: 'feedback' | 'feature' | 'bug', subject: string, message: string) => {
    const response = await api.post(`/feedback?type=${type}`, { subject, message })
    return response.data
  },
  getMyFeedback: async () => {
    const response = await api.get('/feedback')
    return response.data
  }
}

// Notifications API
export const notificationsAPI = {
  // Get user's notifications with optional filters
  getNotifications: async (filters?: {
    status?: 'unread' | 'read' | 'archived'
    type?: 'admin' | 'system' | 'payment' | 'property' | 'account' | 'alert'
    flagged?: boolean
    limit?: number
    startKey?: string
  }) => {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.type) params.append('type', filters.type)
    if (filters?.flagged !== undefined) params.append('flagged', String(filters.flagged))
    if (filters?.limit) params.append('limit', String(filters.limit))
    if (filters?.startKey) params.append('startKey', filters.startKey)

    const response = await api.get(`/notifications?${params.toString()}`)
    return response.data
  },

  // Get unread notification count
  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread-count')
    return response.data
  },

  // Update notification (mark as read/unread, flag/unflag, archive)
  updateNotification: async (
    notificationId: string,
    updates: {
      status?: 'read' | 'unread' | 'archived'
      flagged?: boolean
    }
  ) => {
    const response = await api.patch(`/notifications/${notificationId}`, updates)
    return response.data
  },

  // Delete notification
  deleteNotification: async (notificationId: string) => {
    const response = await api.delete(`/notifications/${notificationId}`)
    return response.data
  },

  // Batch operations
  batchOperation: async (operation: 'markAllRead' | 'deleteAllRead' | 'archiveAll') => {
    const response = await api.post('/notifications/batch', { operation })
    return response.data
  },

  // Send notification (admin only)
  send: async (request: any) => {
    const response = await api.post('/notifications', request)
    return response.data
  }
}

// Notification Templates API (admin only)
export const templatesAPI = {
  // Get all templates
  getAll: async (status?: 'active' | 'draft' | 'archived') => {
    const params = status ? `?status=${status}` : ''
    const response = await api.get(`/notifications/templates${params}`)
    return response.data
  },

  // Get single template
  get: async (templateId: string) => {
    const response = await api.get(`/notifications/templates/${templateId}`)
    return response.data
  },

  // Create template
  create: async (template: any) => {
    const response = await api.post('/notifications/templates', template)
    return response.data
  },

  // Update template
  update: async (templateId: string, updates: any) => {
    const response = await api.put(`/notifications/templates/${templateId}`, updates)
    return response.data
  },

  // Delete template
  delete: async (templateId: string) => {
    const response = await api.delete(`/notifications/templates/${templateId}`)
    return response.data
  },

  // Get available placeholders
  getPlaceholders: async () => {
    const response = await api.get('/notifications/templates/placeholders')
    return response.data
  }
}

// Notification Triggers API (admin only)
export const triggersAPI = {
  // Get all triggers
  getAll: async (status?: 'active' | 'paused' | 'archived') => {
    const params = status ? `?status=${status}` : ''
    const response = await api.get(`/notifications/triggers${params}`)
    return response.data
  },

  // Get single trigger
  get: async (triggerId: string) => {
    const response = await api.get(`/notifications/triggers/${triggerId}`)
    return response.data
  },

  // Create trigger
  create: async (trigger: any) => {
    const response = await api.post('/notifications/triggers', trigger)
    return response.data
  },

  // Update trigger
  update: async (triggerId: string, updates: any) => {
    const response = await api.put(`/notifications/triggers/${triggerId}`, updates)
    return response.data
  },

  // Delete trigger
  delete: async (triggerId: string) => {
    const response = await api.delete(`/notifications/triggers/${triggerId}`)
    return response.data
  },

  // Test trigger for a specific user
  test: async (triggerId: string, userId: string) => {
    const response = await api.post(`/notifications/triggers/${triggerId}/test`, { userId })
    return response.data
  }
}

// Knowledge Hub API
export const knowledgeHubAPI = {
  listCategories: async () => {
    const response = await api.get('/knowledge-hub/categories')
    return response.data
  },

  listArticles: async (categoryId: string, limit?: number, cursor?: string) => {
    const response = await api.get('/knowledge-hub/articles', {
      params: { categoryId, limit, cursor }
    })
    return response.data
  },

  getArticle: async (articleId: string) => {
    const response = await api.get(`/knowledge-hub/articles/${articleId}`)
    return response.data
  },

  // Phase 2: Threads & Replies
  listThreads: async (categoryId: string, limit?: number, cursor?: string) => {
    const response = await api.get('/knowledge-hub/threads', {
      params: { categoryId, limit, cursor }
    })
    return response.data
  },

  getThread: async (threadId: string) => {
    const response = await api.get(`/knowledge-hub/threads/${threadId}`)
    return response.data
  },

  listReplies: async (threadId: string, limit?: number, cursor?: string) => {
    const response = await api.get(`/knowledge-hub/threads/${threadId}/replies`, {
      params: { limit, cursor }
    })
    return response.data
  },

  createThread: async (data: { categoryId: string; title: string; body: string; linkedArticleId?: string }) => {
    const response = await api.post('/knowledge-hub/threads', data)
    return response.data
  },

  createReply: async (threadId: string, body: string) => {
    const response = await api.post(`/knowledge-hub/threads/${threadId}/replies`, { body })
    return response.data
  },

  voteThread: async (threadId: string) => {
    const response = await api.post(`/knowledge-hub/threads/${threadId}/vote`)
    return response.data
  },

  voteReply: async (replyId: string, threadId: string) => {
    const response = await api.post(`/knowledge-hub/replies/${replyId}/vote`, { threadId })
    return response.data
  },

  flagThread: async (threadId: string, reason: string, detail?: string) => {
    const response = await api.post(`/knowledge-hub/threads/${threadId}/flag`, { reason, detail })
    return response.data
  },

  editThread: async (threadId: string, data: { title?: string; body?: string }) => {
    const response = await api.put(`/knowledge-hub/threads/${threadId}`, data)
    return response.data
  },

  acceptAnswer: async (threadId: string, replyId: string) => {
    const response = await api.put(`/knowledge-hub/threads/${threadId}/accept/${replyId}`)
    return response.data
  },

  flagReply: async (replyId: string, threadId: string, reason: string, detail?: string) => {
    const response = await api.post(`/knowledge-hub/replies/${replyId}/flag`, { threadId, reason, detail })
    return response.data
  },
}

// External Data / Enrichment API - Separate functions for each data type
export const externalDataAPI = {
  // Get geo mapping data (postcode -> LSOA, LA, coords)
  getGeoMapping: async (postcode: string) => {
    const response = await api.get('/external-data/enrichment', {
      params: { postcode, skipONS: true, skipCrime: true, skipFlood: true, skipLandRegistry: true, skipTransport: true, skipSchools: true, skipAmenities: true }
    })
    return response.data?.enrichment?.geo
  },

  // Get crime data for a postcode
  getCrime: async (postcode: string) => {
    const response = await api.get('/external-data/enrichment', {
      params: { postcode, skipONS: true, skipFlood: true, skipLandRegistry: true, skipTransport: true, skipSchools: true, skipAmenities: true }
    })
    return response.data?.enrichment?.crime
  },

  // Get ALL amenities in a single API call (cost optimized)
  // IMPORTANT: Use this instead of individual amenity fetchers to reduce API costs by 10x+
  // Returns: cafes, pubs, restaurants, bars, supermarkets, pharmacies, libraries, hospitals, parks, stations, bus_stops, schools
  getAllAmenities: async (postcode: string) => {
    const response = await api.get('/external-data/amenities/all', { params: { postcode } })
    return response.data
  },

  // Get all enrichment data at once (for unlocked properties)
  getFullEnrichment: async (postcode: string, bedrooms: number = 2) => {
    const response = await api.get('/external-data/enrichment', {
      params: { postcode, bedrooms }
    })
    return response.data
  }
}

// Referral API
export const referralAPI = {
  // Get user's referral code and stats
  getReferralData: async () => {
    const response = await api.get('/users/me/referral')
    return response.data
  },

  // Validate referral code
  validateCode: async (code: string) => {
    const response = await api.post('/referrals/validate', { code })
    return response.data
  },

  // Send referral invitation email
  sendInvitation: async (email: string, name?: string) => {
    const response = await api.post('/referrals/invitations', { email, name })
    return response.data
  },

  // Get sent invitations
  getInvitations: async () => {
    const response = await api.get('/referrals/invitations')
    return response.data
  },

  // Beta program invitations
  sendBetaInvitation: async (email: string) => {
    const response = await api.post('/referrals/beta', { email })
    return response.data
  },

  getBetaInvitations: async () => {
    const response = await api.get('/referrals/beta')
    return response.data
  }
}

export const mcpTokensAPI = {
  list: async () => {
    const response = await api.get('/auth/mcp-tokens')
    return response.data
  },
  create: async (name: string) => {
    const response = await api.post('/auth/mcp-tokens', { name })
    return response.data
  },
  revoke: async (tokenId: string) => {
    const response = await api.delete(`/auth/mcp-tokens/${tokenId}`)
    return response.data
  },
}
